import { IIconProps } from '@fluentui/react';
import { TFunction } from 'i18next';
import { TenantProcess } from '../tenantProcessStateBase';

/**
 * What an action does when the user picks it. The kind decides who executes it: the scene, or the
 * Copilot panel through the bridge.
 */
export enum AIActionKind {
  // runs in the scene and reports there; the panel stays out of it
  Inline = 1,
  // opens the panel and sends a ready-made question
  Prompt = 2,
  // opens the panel on a process; starting and stopping stay with the panel's own button
  Process = 3,
  // opens the panel and leaves the choice to the user
  Assistant = 4,
}

/**
 * The reusable definition of an AI action, free of any surface. A template is bound to a surface with
 * concrete parameters (see `aiActionBinding.ts`), so one template serves many contexts.
 *
 * Members are method signatures, not arrow-typed properties: under `strictFunctionTypes` an arrow
 * property is contravariant in `P`, and a list of bindings over different `P` types would not compile.
 */
interface AIActionTemplateBase<P> {
  // dot-namespaced, such as 'text.improve'; with an anchor it stays unique per binding
  id: string;
  kind: AIActionKind;
  getLabel(t: TFunction<string[]>, params: P): string;
  icon?: IIconProps;
  // tells two bindings of this template apart on one surface; becomes part of the binding id
  getAnchor?(params: P): string;
}

export interface AIInlineActionTemplate<P = void> extends AIActionTemplateBase<P> {
  kind: AIActionKind.Inline;
  run(params: P): void | Promise<void>;
}

export interface AIPromptActionTemplate<P = void> extends AIActionTemplateBase<P> {
  kind: AIActionKind.Prompt;
  getPrompt(t: TFunction<string[]>, params: P): string;
  // the question takes the rows a scene selected: the usage words the turn on the selection and the
  // ids travel with the request, so the agent reads the rows
  acceptsSelection?: boolean;
  // with acceptsSelection: offered only while rows are selected, so only a scene menu lists it (the panel
  // never holds a selection)
  requiresSelection?: boolean;
}

export interface AIProcessActionTemplate<P = void> extends AIActionTemplateBase<P> {
  kind: AIActionKind.Process;
  getProcessId(params: P): TenantProcess;
  // the run finishes server-side without the panel watching it, so the panel frees itself meanwhile
  runsInBackground?(params: P): boolean;
  // the start takes the rows a scene selected: a scene menu carries them to the panel (the KPI report, PLAN-070)
  acceptsSelection?: boolean;
}

export interface AIAssistantActionTemplate<P = void> extends AIActionTemplateBase<P> {
  kind: AIActionKind.Assistant;
}

export type AIActionTemplate<P = unknown> =
  | AIInlineActionTemplate<P>
  | AIPromptActionTemplate<P>
  | AIProcessActionTemplate<P>
  | AIAssistantActionTemplate<P>;
